import { useEffect, useMemo, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import "@/App.css";
import "@/theme.css";
import { Toaster } from "@/components/ui/sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { HeroSection } from "@/components/HeroSection";
import { ImpactBand } from "@/components/ImpactBand";
import { ClaraSection, FinalCta } from "@/components/ClaraSection";
import { SeniorSection } from "@/components/SeniorSection";
import { MapSection } from "@/components/MapSection";
import { FilterBar } from "@/components/FilterBar";
import { Directory } from "@/components/Directory";
import { ChatBubble } from "@/components/ChatBubble";
import { FollowUpDialog } from "@/components/FollowUpDialog";
import { CasesSheet } from "@/components/CasesSheet";
import { HowItWorks } from "@/components/HowItWorks";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useOffices } from "@/hooks/useOffices";
import { useCases } from "@/hooks/useCases";
import { useTheme } from "@/hooks/useTheme";
import { useChat } from "@/hooks/useChat";
import { useVoiceGuide } from "@/hooks/useVoiceGuide";
import { VoiceGuideBar } from "@/components/VoiceGuideBar";
import { CATEGORIES } from "@/lib/categories";
import { t, useLang } from "@/lib/i18n";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function App() {
  const { lang, setLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const geo = useGeolocation();
  const { offices, loadingNearby } = useOffices(geo.position);
  const casesApi = useCases();
  const chat = useChat();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [focus, setFocus] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [draft, setDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [casesOpen, setCasesOpen] = useState(false);
  const [followUp, setFollowUp] = useState(null);

  const searched = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return offices;
    return offices.filter((o) => {
      const c = CATEGORIES[o.category];
      return norm(`${o.name} ${o.municipality} ${o.state} ${o.address} ${c?.label} ${c?.keywords}`).includes(q);
    });
  }, [offices, query]);

  const counts = useMemo(() => searched.reduce((acc, o) => ({ ...acc, [o.category]: (acc[o.category] || 0) + 1 }), {}), [searched]);
  const filtered = category === "all" ? searched : searched.filter((o) => o.category === category);
  const guide = useVoiceGuide({ chat, offices, geo, casesApi, openChat: () => setChatOpen(true) });
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current || !offices.length) return;
    autoStarted.current = true;
    if (localStorage.getItem("ac-voice-auto") !== "off") guide.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offices.length]);


  const askAI = (o) => {
    setDraft(t("ai.draft", { name: o.name, cat: CATEGORIES[o.category].label, address: o.address }));
    setChatOpen(true);
  };
  const focusOffice = (o) => {
    setFocus({ ...o, t: Date.now() });
    document.getElementById("mapa")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen bg-ac-bg font-body">
      <button data-testid="skip-voice-mode" onClick={guide.start} className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-ac-primary focus:px-6 focus:py-4 focus:text-sm focus:font-bold focus:text-white">{t("guide.skipLink")}</button>
      <SiteHeader guide={guide} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme} onOpenChat={() => setChatOpen(true)} onOpenCases={() => setCasesOpen(true)} casesCount={casesApi.cases.length} />

      <HeroSection geo={geo} nearest={offices[0]} onOpenChat={() => setChatOpen(true)} />
      <ImpactBand count={offices.length} />

      <section id="mapa-seccion" data-testid="explorer-section" className="relative scroll-mt-16 py-20 lg:py-28">
        <div id="explorador" className="relative mx-auto max-w-[1500px] px-4 sm:px-8">
          <div className="mx-auto mb-12 grid max-w-[1300px] gap-6 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="ac-eyebrow">{t("map.kicker")}</p>
              <h2 className="mt-5 font-wide text-4xl font-normal leading-[1.05] text-ac-ink sm:text-5xl">{t("map.title")}</h2>
            </div>
            <p className="self-end text-lg leading-relaxed text-ac-muted">{t("map.desc")}</p>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:h-[calc(100vh-120px)] lg:min-h-[620px] lg:grid-cols-[minmax(380px,440px)_1fr]">
            <div id="mapa" className="h-[55vh] min-h-[380px] lg:order-2 lg:h-full">
              <MapSection geo={geo} offices={filtered} focus={focus} hoverId={hoverId} onAskAI={askAI} />
            </div>
            <aside id="directorio" data-testid="directory-panel" className="flex min-w-0 flex-col overflow-hidden rounded-3xl border border-ac-line bg-ac-bg lg:order-1">
              <div className="border-b border-ac-line bg-ac-surface p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-display text-lg font-bold text-ac-ink">{t("dir.title")}</h3>
                  <span className="ac-eyebrow flex items-center gap-1.5 !text-[10px]">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[color:var(--ac-eyebrow)]" /> Live
                  </span>
                </div>
                <FilterBar query={query} setQuery={setQuery} category={category} setCategory={setCategory} counts={counts} total={searched.length} />
              </div>
              <div className="hg-scroll flex-1 overflow-y-auto p-4 max-lg:max-h-[80vh]">
                <Directory key={`${category}-${query}`} offices={filtered} loadingNearby={loadingNearby} place={geo.place} onAskAI={askAI} onFocus={focusOffice} onHover={setHoverId} hoverId={hoverId} />
              </div>
            </aside>
          </div>
        </div>
      </section>

      <ClaraSection onOpenChat={() => setChatOpen(true)} />
      <SeniorSection />
      <HowItWorks onOpenChat={() => setChatOpen(true)} onOpenCases={() => setCasesOpen(true)} />
      <FinalCta onOpenChat={() => setChatOpen(true)} />

      <footer className="border-t border-ac-line py-10">
        <div className="mx-auto flex max-w-[1300px] flex-wrap items-center justify-between gap-4 px-4 sm:px-8">
          <span className="font-mark text-base font-bold text-ac-ink">Aclarate</span>
          <p className="text-xs text-ac-muted">{t("footer")}</p>
        </div>
      </footer>

      <VoiceGuideBar guide={guide} />
      <ChatBubble chat={chat} guideActive={guide.active} open={chatOpen} setOpen={setChatOpen} geo={geo} draft={draft} setDraft={setDraft} onFollowUp={setFollowUp} />
      <FollowUpDialog request={followUp} onClose={() => setFollowUp(null)} onCreate={casesApi.create} />
      <CasesSheet open={casesOpen} setOpen={setCasesOpen} casesApi={casesApi} />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
